/**
 * Button — Carnaf design system v1.0
 * Spec: docs/strategy/car-as-place/design-system-2026-05-22/04-components.md § 1
 *
 * Five variants × three sizes. Single text label (gerund/infinitive in Hebrew).
 * Optional leading or trailing icon. Full-width by default within a sticky
 * bottom CTA bar; intrinsic width when inline.
 */

import { forwardRef, useState, useRef, useEffect } from 'react';
import type { ButtonHTMLAttributes, ReactNode, PointerEvent as ReactPointerEvent } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'emergency-call';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  children: ReactNode;
  /** Danger variant: hold-to-confirm. ms to hold. Default 1500. */
  holdMs?: number;
  /** Called once hold-to-confirm completes (only on danger variant). */
  onHoldConfirm?: () => void;
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-10 px-4 text-btn-sm rounded-xl',
  md: 'h-12 px-5 text-btn rounded-2xl',
  lg: 'h-14 px-6 text-btn rounded-2xl',
};

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-carnaf-teal text-white font-medium active:bg-carnaf-teal-pressed disabled:opacity-40 disabled:cursor-not-allowed',
  secondary:
    'bg-transparent border border-carnaf-line-strong text-carnaf-ink font-medium hover:bg-carnaf-paper-elevated active:bg-carnaf-paper-sunk disabled:opacity-40',
  ghost:
    'bg-transparent text-carnaf-teal font-medium hover:bg-carnaf-teal-soft active:bg-carnaf-teal-soft disabled:opacity-40',
  danger: 'bg-carnaf-red text-white font-medium disabled:opacity-40 relative overflow-hidden',
  'emergency-call': 'bg-carnaf-red text-white font-semibold disabled:opacity-40 flex items-center justify-center gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    leadingIcon,
    trailingIcon,
    children,
    className = '',
    onClick,
    holdMs = 1500,
    onHoldConfirm,
    ...rest
  },
  ref,
) {
  const [holdProgress, setHoldProgress] = useState(0);
  const holdStart = useRef<number | null>(null);
  const rafId = useRef<number | null>(null);

  // Hold-to-confirm on danger variant
  useEffect(
    () => () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
    },
    [],
  );

  const startHold = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (variant !== 'danger' || !onHoldConfirm) return;
    // Capture the pointer so a small cursor drift / touch move doesn't fire
    // pointerleave/cancel and abort a hold the user is genuinely performing.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* not all environments support pointer capture */
    }
    holdStart.current = performance.now();
    const tick = () => {
      if (!holdStart.current) return;
      const elapsed = performance.now() - holdStart.current;
      const progress = Math.min(elapsed / holdMs, 1);
      setHoldProgress(progress);
      if (progress >= 1) {
        holdStart.current = null;
        setHoldProgress(0);
        onHoldConfirm();
        return;
      }
      rafId.current = requestAnimationFrame(tick);
    };
    rafId.current = requestAnimationFrame(tick);
  };
  const cancelHold = () => {
    holdStart.current = null;
    setHoldProgress(0);
    if (rafId.current) cancelAnimationFrame(rafId.current);
  };

  const isHoldVariant = variant === 'danger' && !!onHoldConfirm;

  return (
    <button
      ref={ref}
      onClick={isHoldVariant ? undefined : onClick}
      onPointerDown={isHoldVariant ? startHold : undefined}
      onPointerUp={isHoldVariant ? cancelHold : undefined}
      onPointerCancel={isHoldVariant ? cancelHold : undefined}
      onContextMenu={isHoldVariant ? (e) => e.preventDefault() : undefined}
      className={[
        sizeClasses[size],
        variantClasses[variant],
        fullWidth ? 'w-full' : '',
        'inline-flex items-center justify-center gap-2 transition-colors duration-150',
        // Hold-to-confirm needs a positioning context for the progress fill and
        // must suppress mobile long-press (callout/selection/scroll) that would
        // otherwise cancel the hold.
        isHoldVariant ? 'relative overflow-hidden select-none touch-none' : '',
        className,
      ].join(' ')}
      {...rest}
    >
      {/* Hold-to-confirm progress fill */}
      {isHoldVariant && holdProgress > 0 && (
        <span
          aria-hidden="true"
          className="absolute inset-0 bg-white/20 pointer-events-none transition-none"
          style={{ width: `${holdProgress * 100}%` }}
        />
      )}
      <span className="relative inline-flex items-center gap-2">
        {leadingIcon}
        <span>{children}</span>
        {trailingIcon}
      </span>
    </button>
  );
});
